import { Component, Show, createSignal, onMount } from "solid-js";

export type Theme = "light" | "dark" | "system";

export interface ThemeToggleProps {
  /**
   * Current theme value
   * If not provided, component manages its own state via localStorage
   */
  theme?: Theme;
  /**
   * Callback when theme changes
   * If theme prop is provided, this is required for controlled mode
   */
  onThemeChange?: (theme: Theme) => void;
  /**
   * Whether to persist theme to localStorage
   * Only applies in uncontrolled mode
   * @default true
   */
  persist?: boolean;
  /**
   * localStorage key for persisting theme
   * @default "theme"
   */
  storageKey?: string;
  /**
   * Whether to apply theme to document (data-theme attribute)
   * @default true
   */
  applyToDocument?: boolean;
  /**
   * Additional CSS class
   */
  class?: string;
  /**
   * Button size
   * @default "md"
   */
  size?: "sm" | "md" | "lg";
  /**
   * Whether to show text label
   * @default false
   */
  showLabel?: boolean;
  /**
   * Custom light mode label
   * @default "Light"
   */
  lightLabel?: string;
  /**
   * Custom dark mode label
   * @default "Dark"
   */
  darkLabel?: string;
}

const sizeMap = {
  sm: { icon: 16, padding: "0.375rem" },
  md: { icon: 20, padding: "0.5rem" },
  lg: { icon: 24, padding: "0.625rem" },
};

/**
 * ThemeToggle Component
 *
 * A flexible theme toggle button that supports:
 * - Controlled mode (theme + onThemeChange)
 * - Uncontrolled mode (manages own state via localStorage)
 * - Optional document theme application
 * - Multiple sizes
 * - Optional text labels
 *
 * @example
 * ```tsx
 * // Uncontrolled mode (manages its own state)
 * <ThemeToggle />
 *
 * // Controlled mode with external state
 * <ThemeToggle
 *   theme={theme()}
 *   onThemeChange={(newTheme) => setTheme(newTheme)}
 * />
 *
 * // With label
 * <ThemeToggle showLabel size="lg" />
 *
 * // Custom storage key
 * <ThemeToggle storageKey="my-app-theme" />
 * ```
 */
export const ThemeToggle: Component<ThemeToggleProps> = (props) => {
  // Internal state for uncontrolled mode
  const [internalTheme, setInternalTheme] = createSignal<Theme>("light");

  const persist = () => props.persist !== false;
  const storageKey = () => props.storageKey ?? "theme";
  const applyToDocument = () => props.applyToDocument !== false;
  const size = () => props.size ?? "md";
  const lightLabel = () => props.lightLabel ?? "Light";
  const darkLabel = () => props.darkLabel ?? "Dark";

  // Determine if controlled or uncontrolled
  const isControlled = () => props.theme !== undefined;

  // Current theme value (controlled or internal)
  const currentTheme = () => (isControlled() ? props.theme! : internalTheme());

  // Effective theme (resolve "system" to actual theme)
  const effectiveTheme = (): "light" | "dark" => {
    const theme = currentTheme();
    if (theme === "system") {
      if (typeof window !== "undefined") {
        return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
      }
      return "light";
    }
    return theme;
  };

  // Initialize uncontrolled state from storage
  onMount(() => {
    if (!isControlled() && typeof window !== "undefined") {
      const stored = localStorage.getItem(storageKey()) as Theme | null;
      if (stored === "light" || stored === "dark" || stored === "system") {
        setInternalTheme(stored);
        if (applyToDocument()) {
          applyThemeToDocument(stored === "system" ? effectiveTheme() : stored);
        }
      }
    }
  });

  const applyThemeToDocument = (theme: "light" | "dark") => {
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("data-theme", theme);
    }
  };

  const toggleTheme = () => {
    const current = currentTheme();
    const newTheme: Theme = current === "light" ? "dark" : "light";

    if (isControlled()) {
      props.onThemeChange?.(newTheme);
    } else {
      setInternalTheme(newTheme);
      if (persist() && typeof window !== "undefined") {
        localStorage.setItem(storageKey(), newTheme);
      }
    }

    if (applyToDocument()) {
      applyThemeToDocument(newTheme);
    }
  };

  const dimensions = () => sizeMap[size()];

  const buttonStyles = (): Record<string, string> => ({
    background: "transparent",
    border: "1px solid var(--border-light, #e5e7eb)",
    "border-radius": "0.375rem",
    padding: dimensions().padding,
    cursor: "pointer",
    display: "flex",
    "align-items": "center",
    "justify-content": "center",
    gap: props.showLabel ? "0.5rem" : "0",
    color: "var(--text-primary, currentColor)",
  });

  return (
    <button
      onClick={toggleTheme}
      class={`theme-toggle ${props.class ?? ""}`}
      style={buttonStyles()}
      aria-label="Toggle theme"
      title={`Switch to ${effectiveTheme() === "light" ? "dark" : "light"} mode`}
    >
      <Show
        when={effectiveTheme() === "light"}
        fallback={<SunIcon size={dimensions().icon} />}
      >
        <MoonIcon size={dimensions().icon} />
      </Show>
      <Show when={props.showLabel}>
        <span>{effectiveTheme() === "light" ? darkLabel() : lightLabel()}</span>
      </Show>
    </button>
  );
};

/**
 * Moon icon (shown when in light mode, indicating switch to dark)
 */
const MoonIcon: Component<{ size: number }> = (props) => (
  <svg
    width={props.size}
    height={props.size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
  >
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);

/**
 * Sun icon (shown when in dark mode, indicating switch to light)
 */
const SunIcon: Component<{ size: number }> = (props) => (
  <svg
    width={props.size}
    height={props.size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    stroke-width="2"
  >
    <circle cx="12" cy="12" r="5" />
    <line x1="12" y1="1" x2="12" y2="3" />
    <line x1="12" y1="21" x2="12" y2="23" />
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
    <line x1="1" y1="12" x2="3" y2="12" />
    <line x1="21" y1="12" x2="23" y2="12" />
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
  </svg>
);

/**
 * Standalone sun icon export
 */
export const ThemeSunIcon: Component<{ size?: number; class?: string }> = (props) => (
  <SunIcon size={props.size ?? 20} />
);

/**
 * Standalone moon icon export
 */
export const ThemeMoonIcon: Component<{ size?: number; class?: string }> = (props) => (
  <MoonIcon size={props.size ?? 20} />
);
